// @file Composable for undoable actions
import {
  ActionHistoryType,
  redo,
  undo,
  undoSpecificAction,
  type ActionHistoryRecord,
  type SectionActionHistoryData,
} from '@@/bits/action_history_manager'
import { trackEvent } from '@@/bits/analytics'
import device from '@@/bits/device'
import { isActiveElementAnInputField } from '@@/bits/dom'
import { n__, __ } from '@@/bits/intl'
import { SnackbarNotificationType } from '@@/enums'
import {
  displayDeleteWithUndoNotification,
  displayUndoErrorNotification,
  displayUndoRestoredNotification,
} from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import type { DraftPost, Post, Section } from '@@/types'
import KeyboardShortcutTooltip from '@@/vuecomponents/KeyboardShortcutTooltip.vue'
import { useKeyboardShortcutsForUndoRedo } from '@@/vuecomposables/keyboard_shortcuts'
import useLogger from '@@/vuecomposables/logger'
import { ref } from 'vue'

export const DELETE_SNACKBAR_TIMEOUT = 5000
const UNDO_TEXT = __('Undo')
const UNDO_DELETE_ACTION_TEXT_DESKTOP_WINDOWS = ['Ctrl', 'Z']
const UNDO_DELETE_ACTION_TEXT_DESKTOP_MAC = ['⌘', 'Z']
const UNDO_CHANGES_FAILED_TEXT = __("Sorry, we couldn't undo your changes.")
const UNDO_CHANGES_FAILED_ACTION_TEXT = __('Try again')

const logger = useLogger('Undoable', { logLifecycleHooks: false, log: true })

const getUndoDeleteShortcut = (): string[] | undefined => {
  if (!device.desktop) {
    return undefined
  }
  return device.windows ? UNDO_DELETE_ACTION_TEXT_DESKTOP_WINDOWS : UNDO_DELETE_ACTION_TEXT_DESKTOP_MAC
}
const activeSnackbarUids = ref<Record<string, string>>({})

export const useUndoable = (): {
  registerKeyboardShortcuts: () => void
  extractSectionDataForUndoRedo: (section: Section, posts: Post[]) => SectionActionHistoryData
  setDeletedSnackbar: (
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ) => void
  setRestoredSnackbar: (
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ) => void
  handleUndoDeleteSnackbarAction: (actionType: ActionHistoryType, data: ActionHistoryRecord['data']) => Promise<void>
} => {
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceStore = useSurfaceStore()

  async function handleUndo(event: KeyboardEvent): Promise<void> {
    // Don't trigger undo if the user is on an editable element
    if (isActiveElementAnInputField()) return

    // Don't trigger undo if the user has an active draft
    if (surfaceDraftsStore.isAnyDraftActive) return

    const { action, successCallback, errorCallback } = undo(surfaceStore.wallHashid)
    if (action == null || successCallback == null || errorCallback == null) return

    // If the user is on a browser default undoable action (e.g. text input, reopen closed tab), they would trigger
    // both that undoable action and the undoable action we track in our stack
    event.preventDefault()
    event.stopPropagation()

    let undoMutation: () => Promise<ActionHistoryRecord['data']>
    switch (action.type) {
      case ActionHistoryType.DELETE_DRAFTS:
        undoMutation = async () => await surfaceDraftsStore.undoDeleteDrafts(action.data)
        break
      case ActionHistoryType.DELETE_POSTS:
        undoMutation = async () => await surfacePostsStore.undoDeletePosts(action.data)
        break
      case ActionHistoryType.DELETE_SECTIONS:
        undoMutation = async () => await surfaceSectionsStore.undoDeleteSections(action.data)
        break
      default:
        return
    }
    await handleActionMutation({
      action,
      actionMutation: undoMutation,
      successCallback,
      errorCallback,
      successSnackbar: 'restore',
      isKeyboardShortcut: true,
    })
  }

  async function handleRedo(event: KeyboardEvent): Promise<void> {
    // Don't trigger redo if the user is on an editable element
    if (isActiveElementAnInputField()) return

    // We don't check for active draft here unlike handleUndo because the user might want to redo their restored draft that is active
    const { action, successCallback, errorCallback } = redo(surfaceStore.wallHashid)
    if (action == null || successCallback == null || errorCallback == null) return

    // If the user is on a browser default redoable action (e.g. text input, reopen closed tab), they would trigger
    // both that redoable action and the redoable action we track in our stack
    event.preventDefault()
    event.stopPropagation()

    let redoMutation: () => Promise<ActionHistoryRecord['data']>
    switch (action.type) {
      case ActionHistoryType.DELETE_DRAFTS:
        redoMutation = async () => await surfaceDraftsStore.redoDeleteDrafts(action.data)
        break
      case ActionHistoryType.DELETE_POSTS:
        redoMutation = async () => await surfacePostsStore.redoDeletePosts(action.data)
        break
      default:
        return
    }
    await handleActionMutation({
      action,
      actionMutation: redoMutation,
      successCallback,
      errorCallback,
      successSnackbar: 'delete',
      isKeyboardShortcut: true,
    })
  }

  function registerKeyboardShortcuts(): void {
    useKeyboardShortcutsForUndoRedo({
      handleUndo,
      handleRedo,
    })
  }

  function extractSectionDataForUndoRedo(section: Section, posts: Post[]): SectionActionHistoryData {
    return {
      sectionAttributes: {
        id: section.id,
        title: section.title,
        sort_index: section.sort_index,
        color: section.color,
      },
      posts,
    }
  }

  const getDeletedSnackbarMessage = (actionType: ActionHistoryType, count: number): string => {
    switch (actionType) {
      case ActionHistoryType.DELETE_DRAFTS:
        return n__('Draft deleted', '%{count} drafts deleted', count, { count })
      case ActionHistoryType.DELETE_POSTS:
        return n__('Post deleted', '%{count} posts deleted', count, { count })
      case ActionHistoryType.DELETE_SECTIONS:
        return n__('Section deleted', '%{count} sections deleted', count, { count })
      default:
        return ''
    }
  }

  const getRestoredSnackbarMessage = (actionType: ActionHistoryType, count: number): string => {
    switch (actionType) {
      case ActionHistoryType.DELETE_DRAFTS:
        return n__('Draft restored', '%{count} drafts restored', count, { count })
      case ActionHistoryType.DELETE_POSTS:
        return n__('Post restored', '%{count} posts restored', count, { count })
      case ActionHistoryType.DELETE_SECTIONS:
        return n__('Section restored', '%{count} sections restored', count, { count })
      default:
        return ''
    }
  }

  async function handleActionMutation({
    action,
    actionMutation,
    errorCallback,
    successCallback,
    successSnackbar,
    isRetriedOnWeb = false,
    isKeyboardShortcut = false,
  }: {
    action: ActionHistoryRecord
    actionMutation: () => Promise<ActionHistoryRecord['data']>
    successCallback: (updatedData: ActionHistoryRecord['data']) => void
    errorCallback: () => void
    successSnackbar: 'delete' | 'restore'
    isRetriedOnWeb?: boolean
    isKeyboardShortcut?: boolean
  }): Promise<void> {
    let actionMutationSuccess = false
    try {
      const updatedData = await actionMutation()
      successCallback?.(updatedData)
      if (successSnackbar === 'delete') {
        setDeletedSnackbar(action.type, action.id, updatedData)
      } else {
        setRestoredSnackbar(action.type, action.id, updatedData)
      }
      actionMutationSuccess = true
    } catch (e) {
      logger.error(`Error encountered in ${action.type}: ${actionMutation.name}`, e)
      errorCallback()
      if (action.id != null) {
        await dismissSnackbar(action.id)
      }
      setErrorSnackbar(action.id, action.type, action.data, async () => {
        await handleActionMutation({
          action,
          actionMutation,
          successCallback,
          errorCallback,
          successSnackbar,
          isRetriedOnWeb: true,
          isKeyboardShortcut,
        })
      })
    } finally {
      // track both successful and failed undo/redo actions to gauge the success rate
      trackEvent(
        'Undo Redo',
        successSnackbar === 'delete' ? `Redo ${action.type}` : `Undo ${action.type}`,
        null,
        null,
        {
          count: action.data.length,
          isApp: device.app,
          isSuccess: actionMutationSuccess,
          isRetriedOnWeb, // this only accurately tracks on web because native retry retriggers the same snackbar action
          isKeyboardShortcut,
          actionTimeDifference: Date.now() - action.timestamp,
        },
      )
    }
  }

  async function handleUndoDeleteSnackbarAction(
    actionType: ActionHistoryType,
    data: ActionHistoryRecord['data'],
  ): Promise<void> {
    const { action, successCallback, errorCallback } = undoSpecificAction(surfaceStore.wallHashid, actionType, data)
    if (action == null || successCallback == null || errorCallback == null) return

    let undoableAction: () => Promise<ActionHistoryRecord['data']>
    switch (actionType) {
      case ActionHistoryType.DELETE_DRAFTS:
        undoableAction = async () => await surfaceDraftsStore.undoDeleteDrafts(action.data as DraftPost[])
        break
      case ActionHistoryType.DELETE_POSTS:
        undoableAction = async () => await surfacePostsStore.undoDeletePosts(action.data as Post[])
        break
      case ActionHistoryType.DELETE_SECTIONS:
        undoableAction = async () =>
          await surfaceSectionsStore.undoDeleteSections(action.data as SectionActionHistoryData[])
        break
      default:
        return
    }
    await handleActionMutation({
      action,
      actionMutation: undoableAction,
      successCallback,
      errorCallback,
      successSnackbar: 'restore',
      isKeyboardShortcut: false,
    })
  }

  function setDeletedSnackbar(
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ): void {
    if (device.app) {
      if (actionType === ActionHistoryType.DELETE_SECTIONS) {
        // undo delete section is not supported on native yet
        return
      }
      postMessage(
        displayDeleteWithUndoNotification({
          data: data as Exclude<ActionHistoryRecord['data'], SectionActionHistoryData[]>,
          type: actionType,
        }),
      )
      return
    }

    let snackbarTriggerUid: string
    if (
      activeSnackbarUids.value[actionHistoryId] != null &&
      globalSnackbarStore.isSnackbarBeingShown(activeSnackbarUids.value[actionHistoryId])
    ) {
      snackbarTriggerUid = activeSnackbarUids.value[actionHistoryId]
      globalSnackbarStore.updateSnackbar(snackbarTriggerUid, {
        notificationType: SnackbarNotificationType.success,
        message: getDeletedSnackbarMessage(actionType, data.length),
        actionText: UNDO_TEXT,
        actionTextActions: [async () => await handleUndoDeleteSnackbarAction(actionType, data)],
        actionContentSlot: device.desktop ? KeyboardShortcutTooltip : undefined,
        actionContentSlotProps: { shortcut: getUndoDeleteShortcut() },
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    } else {
      snackbarTriggerUid = globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: getDeletedSnackbarMessage(actionType, data.length),
        actionText: UNDO_TEXT,
        actionTextActions: [async () => await handleUndoDeleteSnackbarAction(actionType, data)],
        actionContentSlot: device.desktop ? KeyboardShortcutTooltip : undefined,
        actionContentSlotProps: { shortcut: getUndoDeleteShortcut() },
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    }
    activeSnackbarUids.value[actionHistoryId] = snackbarTriggerUid
  }

  function setRestoredSnackbar(
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ): void {
    // Allow native to display its own snackbar
    if (device.app) {
      postMessage(displayUndoRestoredNotification({ count: data.length, type: actionType }))
      return
    }

    let snackbarTriggerUid: string
    if (
      activeSnackbarUids.value[actionHistoryId] != null &&
      globalSnackbarStore.isSnackbarBeingShown(activeSnackbarUids.value[actionHistoryId])
    ) {
      snackbarTriggerUid = activeSnackbarUids.value[actionHistoryId]
      globalSnackbarStore.updateSnackbar(snackbarTriggerUid, {
        notificationType: SnackbarNotificationType.success,
        message: getRestoredSnackbarMessage(actionType, data.length),
        actionText: undefined,
        actionTextActions: [],
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    } else {
      snackbarTriggerUid = globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: getRestoredSnackbarMessage(actionType, data.length),
        actionText: undefined,
        actionTextActions: [],
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    }
    activeSnackbarUids.value[actionHistoryId] = snackbarTriggerUid
  }

  async function dismissSnackbar(actionHistoryId: string): Promise<void> {
    if (activeSnackbarUids.value[actionHistoryId] == null) return

    globalSnackbarStore.removeSnackbar(activeSnackbarUids.value[actionHistoryId])
    const { [actionHistoryId]: _, ...rest } = activeSnackbarUids.value
    activeSnackbarUids.value = rest
    await new Promise((resolve) => setTimeout(resolve, 500))
  }

  function setErrorSnackbar(
    actionId: string,
    actionType: ActionHistoryType,
    actionData: ActionHistoryRecord['data'],
    retryFn: () => Promise<void>,
  ): void {
    if (device.app) {
      postMessage(displayUndoErrorNotification({ type: actionType, data: actionData as Post[] | DraftPost[] }))
      return
    }

    const newSnackbarTriggerUid = globalSnackbarStore.setSnackbar({
      message: UNDO_CHANGES_FAILED_TEXT,
      notificationType: SnackbarNotificationType.error,
      actionText: UNDO_CHANGES_FAILED_ACTION_TEXT,
      actionTextActions: [async () => await retryFn()],
      timeout: DELETE_SNACKBAR_TIMEOUT,
    })
    activeSnackbarUids.value[actionId] = newSnackbarTriggerUid
  }

  return {
    registerKeyboardShortcuts,
    extractSectionDataForUndoRedo,
    setDeletedSnackbar,
    setRestoredSnackbar,
    handleUndoDeleteSnackbarAction,
  }
}

export { getUndoDeleteShortcut, UNDO_TEXT }
